import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronDown, Users, ArrowUpRight, Shield, Clock, Zap } from 'lucide-react';
import SEO from '@/components/SEO';
import { site, pricingPlans, faqs, comparison, calculator } from '@/data/content';

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function PricingFaq({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 active:scale-[0.99] ${
        isOpen ? 'shadow-md' : ''
      }`}
    >
      <button onClick={onClick} className="flex w-full items-center justify-between text-left" aria-expanded={isOpen}>
        <span className="pr-4 font-semibold text-slate-900">{question}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] pt-3' : 'grid-rows-[0fr]'}`}>
        <div className="overflow-hidden">
          <p className="text-slate-600">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [open, setOpen] = useState<number | null>(null);
  const [agents, setAgents] = useState(5);

  const maxPrice = Math.max(...calculator.competitors.map((c) => c.price));
  const zuriCost = calculator.zuriPrice * agents * 12;
  const topCompetitorCost = maxPrice * agents * 12;
  const annualSavings = topCompetitorCost - zuriCost;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: site.name,
      description: site.description,
      offers: pricingPlans.map((plan) => ({
        '@type': 'Offer',
        name: plan.name,
        description: plan.description,
        url: `${site.baseUrl}/pricing`,
        price: plan.price === 'Custom' ? '0' : plan.price.replace('$', ''),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for ZuriHub. Free, Pro, and Enterprise plans."
        path="/pricing"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Simple, transparent <span className="gradient-text">pricing</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
            Start free with 1 agent. Upgrade to Pro when your team grows. Enterprise for organizations that need voice, SLA, and SSO.
          </p>
        </div>
      </section>

      <section className="py-10" aria-labelledby="trust-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: 'Zap', title: 'Start in minutes', text: 'Free trial. No credit card.' },
              { icon: 'Clock', title: 'Cancel anytime', text: 'No annual lock-in. No hidden fees.' },
              { icon: 'Shield', title: '99.99% uptime SLA', text: 'Enterprise-ready reliability.' },
            ].map((t) => (
              <div key={t.title} className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5">
                {t.icon === 'Zap' ? <Zap className="h-6 w-6 text-brand-600" /> : t.icon === 'Clock' ? <Clock className="h-6 w-6 text-brand-600" /> : <Shield className="h-6 w-6 text-brand-600" />}
                <div>
                  <p className="font-bold text-slate-900">{t.title}</p>
                  <p className="text-sm text-slate-600">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14" aria-labelledby="pricing-plans">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-3xl border p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                  plan.featured ? 'border-brand-200 bg-brand-50 ring-1 ring-brand-200' : 'border-slate-100 bg-white'
                }`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                    Most Popular
                  </span>
                )}
                <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                <p className="mt-2 text-slate-600">{plan.description}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-slate-900">{plan.price}</span>
                  <span className="text-sm text-slate-500">/{plan.period}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.href}
                  className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                    plan.featured ? 'bg-brand-600 text-white hover:bg-brand-700' : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                  }`}
                >
                  {plan.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="savings-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="savings-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                What you save vs the leading tools
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                ZuriHub replaces chat, tickets, knowledge base, and AI at one flat price. Compare for yourself.
              </p>
              <div className="mt-8 rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
                <p className="text-sm font-bold uppercase text-slate-400 tracking-wider">Annual savings vs {calculator.competitors[0].name}</p>
                <p className="mt-2 text-5xl font-extrabold">{fmt.format(annualSavings)}</p>
                <p className="mt-2 text-slate-400">for a team of {agents} agents</p>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start saving <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-xl sm:p-10">
              <div className="flex items-center justify-between">
                <label htmlFor="agents" className="text-sm font-bold text-slate-700">Number of agents</label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={agents}
                    onChange={(e) => {
                      const v = parseInt(e.target.value);
                      setAgents(Math.max(1, Math.min(100, isNaN(v) ? 1 : v)));
                    }}
                    className="w-16 bg-transparent text-right font-bold text-slate-900 outline-none"
                  />
                </div>
              </div>
              <input
                id="agents"
                type="range"
                min={1}
                max={100}
                value={agents}
                onChange={(e) => setAgents(parseInt(e.target.value))}
                className="mt-4 w-full accent-brand-600"
              />
              <div className="mt-8 space-y-5">
                {calculator.competitors.map((comp) => {
                  const theirCost = comp.price * agents;
                  const save = theirCost - calculator.zuriPrice * agents;
                  return (
                    <div key={comp.name}>
                      <div className="flex items-center justify-between text-sm font-semibold">
                        <span className="text-slate-900">{comp.name}</span>
                        <span className="text-slate-500">{fmt.format(theirCost)}/mo</span>
                      </div>
                      <div className="mt-2 h-4 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-4 rounded-full transition-all duration-300 ${comp.color}`} style={{ width: `${(comp.price / maxPrice) * 100}%` }} />
                      </div>
                      <p className="mt-1 text-xs font-bold text-green-600">Save {fmt.format(save)}/mo vs {comp.name}</p>
                    </div>
                  );
                })}
                <div className="rounded-2xl bg-brand-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">ZuriHub Pro</span>
                    <span className="text-2xl font-extrabold text-brand-700">{fmt.format(calculator.zuriPrice * agents)}/mo</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-600">
                    That is {fmt.format(maxPrice * agents)}/mo worth of CX software for {fmt.format(calculator.zuriPrice * agents)}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 id="compare-heading" className="section-title">Why teams choose ZuriHub</h2>
            <p className="section-subtitle mx-auto mt-4">
              One platform, all channels, AI included, and a price that scales with your team.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-6 py-4 font-semibold">Feature</th>
                  <th className="px-6 py-4 font-semibold text-brand-700">ZuriHub</th>
                  <th className="px-6 py-4 font-semibold">Intercom</th>
                  <th className="px-6 py-4 font-semibold">Zendesk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {comparison.rows.map((row) => (
                  <tr key={row.feature}>
                    <td className="px-6 py-4 font-semibold text-slate-900">{row.feature}</td>
                    <td className="px-6 py-4 font-bold text-brand-700">
                      {row.zurihub === true ? 'Yes' : row.zurihub === false ? 'No' : row.zurihub}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {row.intercom === true ? 'Yes' : row.intercom === false ? 'No' : row.intercom}
                    </td>
                    <td className="px-6 py-4 text-slate-600">
                      {row.zendesk === true ? 'Yes' : row.zendesk === false ? 'No' : row.zendesk}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="pricing-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="pricing-faq" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4">Straight answers about pricing, trials, and plans.</p>
          <div className="mt-10 space-y-4">
            {faqs.map((faq, i) => (
              <PricingFaq
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={open === i}
                onClick={() => setOpen(open === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Still have questions?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start a free 14-day Pro trial or talk to sales about Enterprise.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
